/**
 * Create Dataset Page
 *
 * Form to create a new dataset with artifact type configuration.
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Textarea,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateDataset } from '@/lib/api/hooks/datasets';

const artifactTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50),
  description: z.string().max(200).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format'),
});

const datasetSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  artifact_types: z.array(artifactTypeSchema).min(1, 'At least one artifact type is required'),
});

type DatasetFormData = z.infer<typeof datasetSchema>;

const DEFAULT_COLORS = [
  '#EF4444', // red
  '#F59E0B', // amber
  '#10B981', // emerald
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
];

export const Route = createFileRoute('/datasets/new')({
  component: CreateDatasetPage,
});

function CreateDatasetPage() {
  const navigate = useNavigate();
  const createDataset = useCreateDataset();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DatasetFormData>({
    resolver: zodResolver(datasetSchema),
    defaultValues: {
      name: '',
      description: '',
      artifact_types: [
        { name: 'Glitch', description: 'Audio glitches or artifacts', color: DEFAULT_COLORS[0] },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'artifact_types',
  });

  const onSubmit = async (data: DatasetFormData) => {
    try {
      const result = await createDataset.mutateAsync(data);
      navigate({ to: '/datasets/$datasetId', params: { datasetId: result.id } });
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <div className="container-page max-w-2xl py-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="pb-1 pt-3 px-4">
            <h2>Dataset Details</h2>
          </CardHeader>
          <CardBody className="space-y-3 px-4 pb-4 pt-2">
            <Input
              size="sm"
              label="Name"
              labelPlacement="outside"
              placeholder="e.g., Voice Agent QA"
              {...register('name')}
              isInvalid={!!errors.name}
              errorMessage={errors.name?.message}
              isRequired
            />
            <Textarea
              size="sm"
              label="Description"
              labelPlacement="outside"
              placeholder="Optional description..."
              {...register('description')}
              isInvalid={!!errors.description}
              errorMessage={errors.description?.message}
            />
          </CardBody>
        </Card>

        <Card className="border border-default-100 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-1 pt-3 px-4">
            <h2>Artifact Types</h2>
            <Button
              type="button"
              size="sm"
              variant="flat"
              onPress={() =>
                append({
                  name: '',
                  description: '',
                  color: DEFAULT_COLORS[fields.length % DEFAULT_COLORS.length],
                })
              }
            >
              Add Type
            </Button>
          </CardHeader>
          <CardBody className="space-y-3 px-4 pb-4 pt-2">
            {errors.artifact_types?.root && (
              <p className="text-xs text-danger">{errors.artifact_types.root.message}</p>
            )}
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="flex gap-3 rounded-md border border-default-100 p-3"
              >
                <input
                  type="color"
                  {...register(`artifact_types.${index}.color`)}
                  className="h-8 w-8 cursor-pointer rounded border-0 bg-transparent"
                  aria-label="Select artifact type color"
                />
                <div className="flex-1 space-y-2 pt-1">
                  <Input
                    label="Type Name"
                    labelPlacement="outside"
                    placeholder="e.g., Glitch, Pause, Hallucination"
                    {...register(`artifact_types.${index}.name`)}
                    isInvalid={!!errors.artifact_types?.[index]?.name}
                    errorMessage={errors.artifact_types?.[index]?.name?.message}
                    isRequired
                  />
                  <Input
                    label="Description"
                    labelPlacement="outside"
                    placeholder="Optional description..."
                    {...register(`artifact_types.${index}.description`)}
                  />
                </div>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    isIconOnly
                    variant="light"
                    color="danger"
                    size="sm"
                    onPress={() => remove(index)}
                    aria-label="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="flat"
            size="sm"
            onPress={() => navigate({ to: '/datasets' })}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            size="sm"
            isLoading={isSubmitting || createDataset.isPending}
          >
            Create Dataset
          </Button>
        </div>
      </form>
    </div>
  );
}
